import { useEffect, useState } from 'react';

import { BASE_PATH } from '@ai-studio/shared';

import { getView } from '../../engine/prism.js';
import { ApiError, api } from '../../lib/api.js';
import { useWorkspace } from '../../state/workspace.js';

interface PublishedGame {
  slug: string;
  title: string;
  description: string;
  version: number;
  multiplayer: boolean;
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/**
 * The website IDE's Build panel: publish this project to the Prism game
 * library (instantly public; publishing again replaces the previous version).
 * Online play may use join codes (the relay) or a dedicated server the creator
 * runs; a player's copy hosting the game itself is refused (see website/library.ts).
 */
export function PublishPanel() {
  const projectId = useWorkspace((s) => s.projectId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnail, setThumbnail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ url: string; game: PublishedGame } | null>(null);
  const [existing, setExisting] = useState<PublishedGame | null>(null);

  useEffect(() => {
    void api
      .get<{ games: (PublishedGame & { author: string })[] }>('/library/games?mine=1')
      .then((r) => {
        // The library has no project id; a game titled like the project is the likely one.
        const projectName = useWorkspace.getState().projectName;
        const mine = r.games.find((g) => g.title === projectName) ?? null;
        setExisting(mine);
        if (mine) {
          setTitle(mine.title);
          setDescription(mine.description);
        }
      })
      .catch(() => undefined);
  }, [projectId]);

  async function publish() {
    if (!projectId || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      let thumbnailPng: string | undefined;
      if (thumbnail) {
        const shot = await (getView('play') ?? getView('scene'))?.screenshot();
        if (shot && shot.length > 8) thumbnailPng = toBase64(shot);
      }
      const result = await api.post<{ url: string; game: PublishedGame }>(`/projects/${projectId}/publish`, { title: title.trim(), description, thumbnailPng });
      setDone(result);
      setExisting(result.game);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Publishing failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="publish-panel">
      <h3>Publish to the Prism Engine Cloud library</h3>
      <p className="muted">
        Your game appears in the public library right away and plays in any browser. Online play works with join codes through the Prism Engine Cloud relay (up to 8 players) or a dedicated server you run yourself.
      </p>
      {existing && !done && (
        <p className="muted">
          Already published as <a href={`${BASE_PATH}/games/${existing.slug}`} target="_top">{existing.title}</a> (version {existing.version}). Publishing replaces it.
        </p>
      )}
      <label className="field">
        <span>Title</span>
        <input value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} placeholder="My Game" />
      </label>
      <label className="field">
        <span>Description</span>
        <textarea value={description} maxLength={2000} rows={4} onChange={(e) => setDescription(e.target.value)} placeholder="What is it, how do you play?" />
      </label>
      <label className="field field-inline">
        <input type="checkbox" checked={thumbnail} onChange={(e) => setThumbnail(e.target.checked)} />
        <span>Use the current Play / Scene view as the thumbnail</span>
      </label>
      <button className="primary" type="button" disabled={busy || !title.trim()} onClick={() => void publish()}>
        {busy ? 'Publishing…' : existing ? 'Publish new version' : 'Publish'}
      </button>
      {error && (
        <p className="login-error" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p>
          Published. <a href={done.url} target="_top">Open its page</a>
        </p>
      )}
    </div>
  );
}

/** Stands in for a desktop-only panel in the website IDE. */
export function WebsiteNotice({ what }: { what: string }) {
  return (
    <div className="website-notice">
      <p className="muted">{what} is part of the Prism Engine Cloud desktop app and is not available on the website.</p>
    </div>
  );
}
